/* eslint-disable no-redeclare */
/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable default-param-last */
import {
  addChargesDetailAmountsToLeasePriceHistory,
  cleanInputCreate,
  cleanInputUpdate,
  createLeasePriceHistory as createMutation,
  CreateLeasePriceHistoryInput,
  CreateLeasePriceHistoryMutationVariables,
  decideLeasePriceHistories,
  DecideLeasePriceHistoriesInput,
  DecideLeasePriceHistoriesMutationVariables,
  DecideLeasePriceHistoriesResult,
  DEFAULT_GRAPHQL_OBJECT_DEPTH_FOR_SYNC_QUERY,
  deleteLeasePriceHistory as deleteMutation,
  DeleteLeasePriceHistoryMutationVariables,
  getLeasePriceHistory as getLeasePriceHistoryQuery,
  getReadId,
  getTableClientId,
  LeasePriceHistory,
  LeasePriceHistoryDetailInput,
  LooseObject,
  onCreateLeasePriceHistory,
  OnCreateLeasePriceHistorySubscription,
  onDeleteLeasePriceHistory,
  OnDeleteLeasePriceHistorySubscription,
  onUpdateLeasePriceHistory,
  OnUpdateLeasePriceHistorySubscription,
  removeNestedFieldsExcept,
  syncLeasePriceHistories,
  updateLeasePriceHistory as updateMutation,
  UpdateLeasePriceHistoryInput,
} from '@rentguru/commons-utils';
import { deleteAndHideEntityWithFetchBefore, list, mutation, useSubscriptions } from '@up2rent/graphql';
import isNil from 'lodash/isNil';
import { Reducer, createContext, useContext, useEffect, useMemo, useReducer, useRef } from 'react';

import { LeaseExtended, useLeases } from './LeasesContext';
import { useUser } from './UserContext';
import { usePermissions } from './utils/PermissionsContext';

export interface LeasePriceHistoryContext extends LeasePriceHistoryState {
  createLeasePriceHistory: (
    input: CreateLeasePriceHistoryInput | Omit<CreateLeasePriceHistoryInput, 'clientId' | 'readId'>
  ) => Promise<LeasePriceHistory>;
  getLeasePriceHistoriesOfLease: (leaseId: string) => LeasePriceHistory[];
  getLeasePriceHistory: (id: string) => LeasePriceHistory | undefined;
  getLeaseOfLeasePriceHistory: (id: string) => LeaseExtended | null | undefined;
  updateLeasePriceHistory: (updates: UpdateLeasePriceHistoryInput) => Promise<LeasePriceHistory>;
  deleteLeasePriceHistory: (leasePriceHistory: LeasePriceHistory) => Promise<LeasePriceHistory | null>;
  setFetchLeasePriceHistories: () => void;
}

interface LeasePriceHistoryState {
  leasePriceHistories: LeasePriceHistory[] | null;
  loading: boolean;
  shouldFetchLeasePriceHistory: boolean;
  fetchFailed: boolean;
}

enum LeasePriceHistoryActionType {
  ADD = 'ADD',
  DELETE = 'DELETE',
  FETCHED = 'FETCHED',
  FETCH_FAILED = 'FETCH_FAILED',
  IS_FETCHING = 'IS_FETCHING',
  SHOULD_FETCH = 'SHOULD_FETCH',
  UPDATE = 'UPDATE',
}

type Action =
  | {
      type:
        | LeasePriceHistoryActionType.SHOULD_FETCH
        | LeasePriceHistoryActionType.IS_FETCHING
        | LeasePriceHistoryActionType.FETCH_FAILED;
    }
  | {
      type: LeasePriceHistoryActionType.ADD;
      payload: { leasePriceHistory: LeasePriceHistory };
    }
  | {
      type: LeasePriceHistoryActionType.FETCHED;
      payload: { leasePriceHistories: LeasePriceHistory[] };
    }
  | {
      type: LeasePriceHistoryActionType.UPDATE;
      payload: { leasePriceHistory: LeasePriceHistory };
    }
  | {
      type: LeasePriceHistoryActionType.DELETE;
      payload: { id: string };
    };

const initialState: LeasePriceHistoryState = {
  loading: false,
  leasePriceHistories: null,
  shouldFetchLeasePriceHistory: false,
  fetchFailed: false,
};

const leasePriceHistoryReducer = (state: LeasePriceHistoryState, action: Action): LeasePriceHistoryState => {
  switch (action.type) {
    case LeasePriceHistoryActionType.SHOULD_FETCH:
      // Check //lastFetchForAgencyRates
      if (state.loading || state.shouldFetchLeasePriceHistory) {
        return state;
      }
      return {
        ...state,
        shouldFetchLeasePriceHistory: true,
        fetchFailed: false,
      };
    case LeasePriceHistoryActionType.IS_FETCHING:
      return {
        ...state,
        loading: true,
      };
    case LeasePriceHistoryActionType.FETCHED:
      // Build List with merged object
      return {
        ...state,
        loading: false,
        shouldFetchLeasePriceHistory: false,
        leasePriceHistories: action.payload.leasePriceHistories,
      };
    case LeasePriceHistoryActionType.FETCH_FAILED:
      return {
        ...state,
        loading: false,
        shouldFetchLeasePriceHistory: false,
        fetchFailed: true,
      };
    case LeasePriceHistoryActionType.ADD:
      return {
        ...state,
        leasePriceHistories: [...(state.leasePriceHistories ?? []), action.payload.leasePriceHistory],
      };
    case LeasePriceHistoryActionType.UPDATE:
      if (!state.leasePriceHistories) {
        return state;
      }
      return {
        ...state,
        leasePriceHistories: state.leasePriceHistories.map((leasePriceHistory) => {
          if (leasePriceHistory.id === action.payload.leasePriceHistory.id) {
            return action.payload.leasePriceHistory;
          }
          return leasePriceHistory;
        }),
      };
    case LeasePriceHistoryActionType.DELETE:
      if (!state.leasePriceHistories) {
        return state;
      }
      return {
        ...state,
        leasePriceHistories: state.leasePriceHistories.filter(
          (leasePriceHistory) => leasePriceHistory.id !== action.payload.id
        ),
      };
    default:
      return state;
  }
};

const LEASE_PRICE_HISTORY_RELATIONS_TO_REMOVE = ['communication', 'lease'];
const LEASE_PRICE_HISTORY_NESTED_FIELDS_TO_KEEP = ['amountDetails'];

const LEASE_PRICE_HISTORIES_QUERY = addChargesDetailAmountsToLeasePriceHistory(
  removeNestedFieldsExcept(
    syncLeasePriceHistories,
    DEFAULT_GRAPHQL_OBJECT_DEPTH_FOR_SYNC_QUERY,
    LEASE_PRICE_HISTORY_RELATIONS_TO_REMOVE,
    LEASE_PRICE_HISTORY_NESTED_FIELDS_TO_KEEP
  )
);

export const fetchLeasePriceHistories = async (
  by: 'byClientId' | 'byLease',
  byValue: string,
  additionalFilter?: LooseObject
): Promise<LeasePriceHistory[]> => {
  const indexName = by === 'byClientId' ? 'clientId' : 'leaseId';
  return await list<LeasePriceHistory>(LEASE_PRICE_HISTORIES_QUERY, indexName, byValue, additionalFilter);
};

export const decideLeasePriceHistoriesOfSelection = (
  input: DecideLeasePriceHistoriesInput
): Promise<DecideLeasePriceHistoriesResult> =>
  mutation<DecideLeasePriceHistoriesResult, DecideLeasePriceHistoriesMutationVariables>(decideLeasePriceHistories, {
    input,
  });

export const LeasePriceHistoryContext = createContext<LeasePriceHistoryContext | null>(null);

export const LeasePriceHistoryContextProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer<Reducer<LeasePriceHistoryState, Action>>(leasePriceHistoryReducer, initialState);
  const { leases, leasesLoading, getLease } = useLeases();
  const { leasesCreationDelete } = usePermissions();
  const { clientId, rootUser } = useUser();

  const setFetchLeasePriceHistories = () => {
    dispatch({ type: LeasePriceHistoryActionType.SHOULD_FETCH });
  };

  const leasesById = useMemo<Map<string, LeaseExtended>>(
    () => new Map(leases.map((lease) => [lease.id, lease])),
    [leases]
  );

  const leasePriceHistories = useMemo<LeasePriceHistory[] | null>(() => {
    if (isNil(state.leasePriceHistories)) {
      return state.leasePriceHistories;
    }
    const scopedLeasePriceHistories = rootUser
      ? state.leasePriceHistories
      : state.leasePriceHistories.filter((leasePriceHistory) => leasesById.has(leasePriceHistory.leaseId));
    return scopedLeasePriceHistories.map((leasePriceHistory) => ({
      ...leasePriceHistory,
      lease: leasesById.get(leasePriceHistory.leaseId),
    }));
  }, [state.leasePriceHistories, leasesById, rootUser]);

  const loading = state.loading || (!rootUser && Boolean(leasesLoading));

  useEffect(() => {
    const fetchAndSet = async () => {
      dispatch({ type: LeasePriceHistoryActionType.IS_FETCHING });
      try {
        const result = await fetchLeasePriceHistories('byClientId', getTableClientId(clientId!, 'LeasePriceHistory'));
        dispatch({ type: LeasePriceHistoryActionType.FETCHED, payload: { leasePriceHistories: result } });
      } catch (error) {
        console.error('[leasePriceHistories] fetching lease price histories failed', error);
        dispatch({ type: LeasePriceHistoryActionType.FETCH_FAILED });
      }
    };
    if (state.shouldFetchLeasePriceHistory) fetchAndSet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.shouldFetchLeasePriceHistory]);

  useSubscriptions<
    OnCreateLeasePriceHistorySubscription,
    OnUpdateLeasePriceHistorySubscription,
    OnDeleteLeasePriceHistorySubscription
  >(
    onCreateLeasePriceHistory,
    onUpdateLeasePriceHistory,
    onDeleteLeasePriceHistory,
    (data) => {
      dispatch({
        type: LeasePriceHistoryActionType.ADD,
        payload: { leasePriceHistory: data.onCreateLeasePriceHistory as LeasePriceHistory },
      });
    },
    (data) => {
      dispatch({
        type: LeasePriceHistoryActionType.UPDATE,
        payload: { leasePriceHistory: data.onUpdateLeasePriceHistory as LeasePriceHistory },
      });
    },
    (data) => {
      const { id } = data.onDeleteLeasePriceHistory as LeasePriceHistory;
      dispatch({
        type: LeasePriceHistoryActionType.DELETE,
        payload: { id },
      });
    }
  );

  const createLeasePriceHistory = async (
    input: CreateLeasePriceHistoryInput | Omit<CreateLeasePriceHistoryInput, 'clientId' | 'readId'>
  ): Promise<LeasePriceHistory> => {
    const leasePriceHistory = await mutation<LeasePriceHistory, CreateLeasePriceHistoryMutationVariables>(
      createMutation,
      {
        input: {
          ...(cleanInputCreate(input) as CreateLeasePriceHistoryInput),
          clientId: getTableClientId(clientId!, 'LeasePriceHistory'),
          readId: getReadId(clientId!, 'LeasePriceHistory'),
        },
      }
    );
    return leasePriceHistory;
  };

  const getLeasePriceHistoriesOfLease = (leaseId: string) => {
    if (isNil(leasePriceHistories)) {
      return [];
    }
    return leasePriceHistories.filter((leasePriceHistory) => leasePriceHistory.leaseId === leaseId);
  };

  const updateLeasePriceHistory = async (updates: UpdateLeasePriceHistoryInput): Promise<LeasePriceHistory> => {
    const cleanInput = { ...cleanInputUpdate(updates, false) };
    if (updates.amountDetails) {
      cleanInput.amountDetails = updates.amountDetails.map((amountDetail) =>
        cleanInputUpdate(amountDetail)
      ) as LeasePriceHistoryDetailInput[];
    }
    const result = await mutation<LeasePriceHistory>(updateMutation, {
      input: cleanInput,
    });
    return result;
  };

  const deleteLeasePriceHistory = async (leasePriceHistory: LeasePriceHistory): Promise<LeasePriceHistory | null> => {
    if (!leasesCreationDelete) {
      return null;
    }
    const result = await deleteAndHideEntityWithFetchBefore<
      LeasePriceHistory,
      DeleteLeasePriceHistoryMutationVariables
    >(leasePriceHistory, getLeasePriceHistoryQuery, deleteMutation);

    return result;
  };

  const getLeasePriceHistory = (id: string) => {
    const leasePriceHistory = (leasePriceHistories ?? []).find(
      (leasePriceHistoryElement) => leasePriceHistoryElement.id === id
    );
    return leasePriceHistory;
  };

  const getLeaseOfLeasePriceHistory = (id: string) => {
    const leasePriceHistory = getLeasePriceHistory(id);
    if (isNil(leasePriceHistory)) {
      return undefined;
    }
    return getLease(leasePriceHistory.leaseId);
  };

  const values = useMemo(
    () => ({
      ...state,
      leasePriceHistories,
      loading,
      createLeasePriceHistory,
      getLeasePriceHistoriesOfLease,
      getLeasePriceHistory,
      updateLeasePriceHistory,
      deleteLeasePriceHistory,
      getLeaseOfLeasePriceHistory,
      setFetchLeasePriceHistories,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state, leasePriceHistories, loading]
  );

  return <LeasePriceHistoryContext.Provider value={values}>{children}</LeasePriceHistoryContext.Provider>;
};

export const useLeasePriceHistories = (): LeasePriceHistoryContext => {
  const firstRender = useRef<boolean>(false);
  const context = useContext<LeasePriceHistoryContext | null>(LeasePriceHistoryContext);

  if (isNil(context)) {
    throw new Error('`useLeasePriceHistories` hook must be used within a `LeasePriceHistoryContext` component');
  }
  useEffect(() => {
    if (!firstRender.current && isNil(context.leasePriceHistories)) {
      context.setFetchLeasePriceHistories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstRender]);

  return context;
};
